// External-learner email-link client (E1 US-1.3). Wraps the two CLMS-owned
// endpoints and maps their HTTP outcomes to the sign-in view's states, so the
// pages stay declarative and the mapping is unit-testable in one place.
//
// Backend contract (backend/src/Clms.Api/Security/AuthEndpoints.cs):
//   POST /api/auth/email-link/request  -> 202 sent · 400 invalid · 404 unknown tenant · 429 rate-limited
//   POST /api/auth/email-link/redeem   -> 200 session · 410 expired/used · 404 unknown tenant · 429 rate-limited
// The request endpoint deliberately never reveals whether an address exists.
//
// THE 429 IS ITS OWN OUTCOME, AND NOT THE RESEND COOLDOWN (task da77004b). Both endpoints are behind a
// per-address rate limit (RateLimitPolicies.PublicEmailLink), because their 404 for an unknown tenant is
// the same slug oracle the sign-in branding read is limited for. That refusal has to stay distinguishable
// from the two things it is NOT:
//   - The RESEND COOLDOWN (AuthOptions.EmailLink.ResendCooldownSeconds) is silent by design: a request
//     inside it answers 202 like every other outcome, so it renders "check your email" and never arrives
//     here. A 429 means the opposite — nothing was done with this request at all.
//   - A GENERIC failure. Mapping 429 to `error` (which is where it fell before this task) told a learner
//     who simply asked twice that something had gone wrong, and gave them nothing to do about it.
// A refused REDEEM spends no token either — the limiter short-circuits before the handler — so retrying
// the same link after the window is the right advice, which is why this is not folded into `expired`.
// That is measured in apps/web/e2e/realstack/public-email-link-budget.realstack.spec.ts: a real token
// refused with a 429 still opens a session from an address with permits left.

import type { components } from "@clms/contracts";
import { apiFetch, ApiError } from "../lib/api";
import { parseRespondPath } from "./questionnaireReturnTo";

type EmailLinkRequestBody = components["schemas"]["EmailLinkRequest"];
type RedeemBody = components["schemas"]["RedeemEmailLinkRequest"];

/**
 * Opaque learner session issued on a successful redeem — the generated contract type, not a hand-written
 * copy of it: the redeem operation now declares its 200 body, so `make codegen` emits this shape and a
 * local duplicate could drift from the API without anything failing.
 */
type LearnerSessionResponse = components["schemas"]["LearnerSessionResponse"];

export type RequestOutcome = "sent" | "invalid-email" | "unknown-tenant" | "rate-limited" | "error";

export type RedeemOutcome =
  | { kind: "ok"; sessionToken: string; expiresAt?: string }
  | { kind: "expired" }
  | { kind: "unknown-tenant" }
  | { kind: "rate-limited" }
  | { kind: "error" };

/** Lightweight client-side sanity check; the backend remains the authority. */
export function isEmailish(value: string): boolean {
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * Request a one-time sign-in link for `email`. Never throws.
 *
 * `returnTo` is where the redeemed link should land — a questionnaire respond path, for the learner who
 * came here from a questionnaire login link that no longer works (task ec54522c). It is validated here as
 * well as on the backend: this call is made from a page whose own URL carried the value, and
 * `parseRespondPath` is what keeps anything but that one shape out of the request body. Omitted or
 * unrecognised, the link lands on the home view, which is what it has always done.
 */
export async function requestEmailLink(
  email: string,
  returnTo?: string | null,
  signal?: AbortSignal,
): Promise<RequestOutcome> {
  const body: EmailLinkRequestBody = { email: email.trim() };
  const safeReturnTo = parseRespondPath(returnTo);
  if (safeReturnTo) body.returnTo = safeReturnTo;
  try {
    await apiFetch("/api/auth/email-link/request", { method: "POST", body, signal });
    return "sent";
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 400) return "invalid-email";
      if (err.status === 404) return "unknown-tenant";
      if (err.status === 429) return "rate-limited";
    }
    return "error";
  }
}

/** Redeem a one-time token into a learner session. Never throws. */
export async function redeemEmailLink(token: string, signal?: AbortSignal): Promise<RedeemOutcome> {
  const body: RedeemBody = { token };
  try {
    const session = await apiFetch<LearnerSessionResponse>("/api/auth/email-link/redeem", {
      method: "POST",
      body,
      signal,
    });
    // The opaque session token is the learner's bearer for every subsequent API call — a redeem that
    // returned no token is unusable, so treat that as an error rather than a broken "signed-in" state.
    if (!session?.sessionToken) return { kind: "error" };
    return { kind: "ok", sessionToken: session.sessionToken, expiresAt: session.expiresAt };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 410) return { kind: "expired" };
      if (err.status === 404) return { kind: "unknown-tenant" };
      if (err.status === 429) return { kind: "rate-limited" };
    }
    return { kind: "error" };
  }
}
